import { Button } from "$components/button/button";
import { Chip } from "$components/chip";
import {
  CloseIcon,
  EmptyCircleIcon,
  HalfFilledCircleIcon,
  TickInCircleIcon,
} from "$components/icons";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { PALETTE } from "$global-providers/mui-provider/palette";
import {
  useVaccinationsService,
  VaccinationNudgeType,
} from "$routes/patient/[:patientId]/_lib/vaccinations-service/provider";
import {
  Box,
  Table as MuiTable,
  styled,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { differenceInDays, format, isBefore, min } from "date-fns";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { VaccinationNudge } from "../vaccinations/_lib/vaccination-form-nudge";
import {
  type VaccinationSchedule,
  VaccinationScheduleDisease,
  type VaccinationScheduleDose,
} from "./vaccinations-service/vaccination-schedule";

const TabHeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const Column = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",
}));

const CloseButton = styled(Button)(() => ({
  padding: 0,
  height: "fit-content",
}));

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold as any,
}));

const NonDueText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

const DoseCompleteIconsContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  height: "100%",
});

const NextDueMessageContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "flex-end",
  width: "100%",
  height: "100%",
});

const StyledTableCell = styled(TableCell)<Theme, { noBorder: boolean }>(
  ({ theme, noBorder }) => ({
    borderBottom: noBorder ? "none" : `1px solid ${theme.palette.grey[300]}`,
  }),
);

const StyledTableRow = styled(TableRow)<Theme, { isExpandable: boolean }>(
  ({ theme, isExpandable }) => ({
    cursor: isExpandable ? "pointer" : "default",
  }),
);

const TableCellWithChevron = styled(TableCell)<Theme, { noBorder?: boolean }>(
  ({ theme, noBorder }) => ({
    paddingRight: theme.spacing(6), // Consistent padding for chevron space
    borderBottom: noBorder ? "none" : `1px solid ${theme.palette.grey[300]}`,
  }),
);

const ExpandButton = styled(Button)({
  padding: 0,
  position: "absolute",
  right: 16,
});

export enum VaccineStatus {
  DUE = "DUE",
  COMPLETED = "COMPLETED",
  MISSING = "MISSING",

  INCOMPLETE = "INCOMPLETE",
}
export enum VaccineDoseCompletionStatus {
  COMPLETE = "COMPLETE",
  PARTIALLY_COMPLETE = "PARTIALLY_COMPLETE",
  NOT_COMPLETE = "NOT_COMPLETE",
}

export const VaccineGroups: {
  [diseaseKey: string]: {
    displayName: string;
    diseases: {
      diseaseName: string;
      /* for some diseases, we split up the doses for display in different VaccineGroups.
      This means they need to be separated for the calculations. The indices refer to the
      vaccinationSchedule doses e.g. VaccinationSchedule.yellowfever.doses   */
      vaccinationScheduleDoseIndices: number[];
    }[];
  };
} = {
  IPV: {
    displayName: "IPV",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.IPV,
        vaccinationScheduleDoseIndices: [0, 1],
      },
    ],
  },
  VITA: {
    displayName: "Vitamin A",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.VITA,
        vaccinationScheduleDoseIndices: [0, 1],
      },
    ],
  },
  MEASLES: {
    displayName: "Measles",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.MEASLES,
        vaccinationScheduleDoseIndices: [0, 1],
      },
    ],
  },
  YELLOWFEVER: {
    displayName: "Yellow Fever",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.YELLOWFEVER,
        vaccinationScheduleDoseIndices: [0],
      },
    ],
  },
  MENINGITIS: {
    displayName: "Meningitis",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.MENINGITIS,
        vaccinationScheduleDoseIndices: [0],
      },
    ],
  },
  HPV: {
    displayName: "HPV",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.HPV,
        vaccinationScheduleDoseIndices: [0],
      },
    ],
  },
  BCG: {
    displayName: "BCG",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.BCG,
        vaccinationScheduleDoseIndices: [0],
      },
    ],
  },
  OPV: {
    displayName: "OPV",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.OPV,
        vaccinationScheduleDoseIndices: [0, 1, 2, 3],
      },
    ],
  },
  PENTA: {
    displayName: "Penta",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.HIB,
        vaccinationScheduleDoseIndices: [0, 1, 2],
      },
      {
        diseaseName: VaccinationScheduleDisease.HEPB,
        vaccinationScheduleDoseIndices: [1, 2, 3],
      },
      {
        diseaseName: VaccinationScheduleDisease.TETANUS,
        vaccinationScheduleDoseIndices: [0, 1, 2],
      },
      {
        diseaseName: VaccinationScheduleDisease.PERTUSSIS,
        vaccinationScheduleDoseIndices: [0, 1, 2],
      },
      {
        diseaseName: VaccinationScheduleDisease.DIPHTHERIA,
        vaccinationScheduleDoseIndices: [0, 1, 2],
      },
    ],
  },
  PCV: {
    displayName: "PCV",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.PCV,
        vaccinationScheduleDoseIndices: [0, 1, 2],
      },
    ],
  },
  ROTAVIRUS: {
    displayName: "Rotavirus",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.ROTAVIRUS,
        vaccinationScheduleDoseIndices: [0, 1, 2],
      },
    ],
  },
  HEPB: {
    displayName: "Hepatitis B",
    diseases: [
      {
        diseaseName: VaccinationScheduleDisease.HEPB,
        vaccinationScheduleDoseIndices: [0],
      },
    ],
  },
} as const;

const vaccineDoseStatusCalculationPriority = [
  VaccineStatus.MISSING,
  VaccineStatus.DUE,
  VaccineStatus.INCOMPLETE,
  VaccineStatus.COMPLETED,
] as const;

const vaccineDoseStatusSortOrder = {
  [VaccineStatus.DUE]: 1,
  [VaccineStatus.INCOMPLETE]: 2,
  [VaccineStatus.COMPLETED]: 3,
  [VaccineStatus.MISSING]: 4,
} as const;

const vaccineStatusToStatusChipMapper = {
  [VaccineStatus.DUE]: <Chip label="Due" color="primary" />,
  [VaccineStatus.COMPLETED]: <Chip label="Complete" color="brand" />,
  [VaccineStatus.MISSING]: <Chip label="Missing" color="grey" />,
  [VaccineStatus.INCOMPLETE]: null,
} as const;

const doseStatusToIconMapper = {
  [VaccineDoseCompletionStatus.COMPLETE]: (
    <TickInCircleIcon
      color={PALETTE.background.default}
      backgroundColor={PALETTE.brand.main}
    />
  ),
  [VaccineDoseCompletionStatus.NOT_COMPLETE]: (
    <EmptyCircleIcon color={PALETTE.brand.main} />
  ),
  [VaccineDoseCompletionStatus.PARTIALLY_COMPLETE]: (
    <HalfFilledCircleIcon color={PALETTE.brand.main} />
  ),
} as const;

const today = new Date();
export const getVaccinationTableRowDetails = (
  doses: VaccinationScheduleDose[],
  displayName: string,
  diseaseId: string,
) => {
  const remainingDoses = doses.filter((dose) => !dose.completedDate);

  const doseStatuses = doses.map((dose) =>
    dose.completedDate
      ? VaccineDoseCompletionStatus.COMPLETE
      : VaccineDoseCompletionStatus.NOT_COMPLETE,
  );

  if (!remainingDoses.length)
    return {
      message: "",
      status: VaccineStatus.COMPLETED,
      nextDueDate: null,
      displayName,
      id: diseaseId,
      doseStatuses,
    };

  const nextDueDose = remainingDoses[0];
  const nextDueDate = nextDueDose.dueDate ? nextDueDose.dueDate : null;
  const formattedNextDueDate = nextDueDate
    ? format(nextDueDate, "dd/MM/yyyy")
    : "";
  const isDue = nextDueDate && differenceInDays(nextDueDate, today) <= 0;

  const hasMissing = doses.some(
    (dose) =>
      !dose.completedDate &&
      dose.missingDate &&
      isBefore(dose.missingDate, today),
  );

  return {
    message: formattedNextDueDate,
    status: hasMissing
      ? VaccineStatus.MISSING
      : isDue
        ? VaccineStatus.DUE
        : VaccineStatus.INCOMPLETE,
    nextDueDate: nextDueDose.dueDate,
    displayName,
    id: diseaseId,
    doseStatuses,
  };
};

const getDoseStatusesAndOverallDueDate = (
  doses: VaccinationScheduleDose[][],
): { doseStatuses: VaccineDoseCompletionStatus[]; nextDueDate: Date } => {
  let nextDueDate: Date | null = null;
  // currently all vaccines within a vaccine group have the same number of doses
  // so this is a convenient way to get the number of doses to map over them
  const numberOfDosesInVaccineGroup = doses[0].length;
  const doseStatuses = Array.from(
    { length: numberOfDosesInVaccineGroup },
    (_, doseIndex) => {
      const dosesAtIndex = doses.map((diseaseDoses) => diseaseDoses[doseIndex]);
      const completions = dosesAtIndex.map((dose) => {
        if (dose.dueDate) {
          nextDueDate = nextDueDate
            ? min([nextDueDate, dose.dueDate])
            : dose.dueDate;
        }

        return !!dose?.completedDate;
      });

      if (completions.every((status) => status)) {
        return VaccineDoseCompletionStatus.COMPLETE;
      }
      if (completions.some((status) => status)) {
        return VaccineDoseCompletionStatus.PARTIALLY_COMPLETE;
      }
      return VaccineDoseCompletionStatus.NOT_COMPLETE;
    },
  );
  return { doseStatuses, nextDueDate };
};

export const VaccinationsSchedule: FC<
  { location?: "sidePanel"; onClose: () => void } | { location?: "page" }
> = (props = { location: "page" }) => {
  const [vaccineScheduleFlag] = useFeatureFlag(
    featureFlags.immunisations.schedule,
  );
  const [vaccineNudgesFlag] = useFeatureFlag(featureFlags.immunisations.nudges);
  const { getPatientVaccinationSchedule, getUnusualVaccineNames } =
    useVaccinationsService(); // TODO: does patient need to come from the service?

  const patientVaccinationSchedule = vaccineScheduleFlag
    ? getPatientVaccinationSchedule()
    : null;

  const unusualVaccineDisplayNames =
    vaccineScheduleFlag && vaccineNudgesFlag ? getUnusualVaccineNames() : [];
  return (
    <Column>
      <TabHeaderContainer>
        <Typography variant="h5">
          <FormattedMessage
            id="patient.immunisations.schedule.header"
            defaultMessage="Vaccination status"
          />
        </Typography>
        {props.location === "sidePanel" && (
          <CloseButton
            variant="text"
            onClick={props.onClose}
            startIcon={<CloseIcon color="primary" />}
          />
        )}
      </TabHeaderContainer>
      {unusualVaccineDisplayNames.length ? (
        <VaccinationNudge
          vaccines={unusualVaccineDisplayNames}
          type={VaccinationNudgeType.UNUSUAL_HISTORY}
        />
      ) : null}
      <VaccinationScheduleTable
        vaccinationSchedule={patientVaccinationSchedule}
      />
    </Column>
  );
};

export const VaccinationScheduleTable = ({
  vaccinationSchedule,
}: {
  vaccinationSchedule: VaccinationSchedule;
}) => {
  const sortedRows = useMemo(
    () =>
      Object.values(VaccineGroups)
        .map((vaccine) => {
          const diseaseDetails: VaccinationScheduleDose[][] =
            vaccine.diseases.map(
              (disease: {
                diseaseName: string;
                vaccinationScheduleDoseIndices: readonly number[];
              }) => {
                return vaccinationSchedule[disease.diseaseName].doses.filter(
                  (_, idx) =>
                    disease.vaccinationScheduleDoseIndices.includes(idx),
                );
              },
            );
          const { doseStatuses, nextDueDate } =
            getDoseStatusesAndOverallDueDate(diseaseDetails);
          const diseaseRowDetails = diseaseDetails.map((doses, index) =>
            getVaccinationTableRowDetails(
              doses,
              vaccinationSchedule[vaccine.diseases[index].diseaseName]
                .displayName,
              vaccine.diseases[index].diseaseName,
            ),
          );

          const hasMultipleDueDates =
            vaccine.diseases.length > 1 &&
            new Set(
              diseaseRowDetails.map((item) => item.nextDueDate?.getTime()),
            ).size > 1;
          const overallStatus =
            vaccineDoseStatusCalculationPriority.find((status) =>
              diseaseRowDetails.some((item) => item.status === status),
            ) ?? VaccineStatus.COMPLETED;

          return {
            componentDiseases: diseaseRowDetails,
            overallStatus,
            doseStatuses,
            nextDueDate,
            hasMultipleDueDates,
            displayName: vaccine.displayName,
            id: vaccine.displayName,
            isExpandable:
              vaccine.diseases.length > 1 &&
              doseStatuses.some(
                (status) =>
                  status === VaccineDoseCompletionStatus.PARTIALLY_COMPLETE,
              ),
          };
        })
        .sort((a, b) => {
          if (
            vaccineDoseStatusSortOrder[a.overallStatus] !==
            vaccineDoseStatusSortOrder[b.overallStatus]
          ) {
            return (
              vaccineDoseStatusSortOrder[a.overallStatus] -
              vaccineDoseStatusSortOrder[b.overallStatus]
            );
          }

          if (!a.nextDueDate) return 1;
          if (!b.nextDueDate) return -1;

          return (
            new Date(a.nextDueDate).getTime() -
            new Date(b.nextDueDate).getTime()
          );
        }),
    [vaccinationSchedule],
  );

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  if (!vaccinationSchedule) return <Typography>loading..</Typography>;

  // Modify the table rendering to flatten the rows
  const renderRows = () => {
    return sortedRows.flatMap((row) => {
      const isExpanded = expandedRows.has(row.id);
      const baseRow = (
        <StyledTableRow
          key={row.id}
          isExpandable={row.isExpandable}
          onClick={
            row.isExpandable
              ? () => {
                  const newExpanded = new Set(expandedRows);
                  isExpanded
                    ? newExpanded.delete(row.id)
                    : newExpanded.add(row.id);
                  setExpandedRows(newExpanded);
                }
              : undefined
          }
        >
          <StyledTableCell noBorder={isExpanded}>
            <BoldText variant="body1">{row.displayName}</BoldText>
          </StyledTableCell>
          <StyledTableCell noBorder={isExpanded}>
            <DoseCompleteIconsContainer>
              {row.doseStatuses.map((doseStatus, index) => (
                <span key={`${index}-${doseStatus}`}>
                  {doseStatusToIconMapper[doseStatus]}
                </span>
              ))}
            </DoseCompleteIconsContainer>
          </StyledTableCell>
          <TableCellWithChevron noBorder={isExpanded}>
            <NextDueMessageContainer>
              {vaccineStatusToStatusChipMapper[row.overallStatus]}
              {row.hasMultipleDueDates ? (
                <BoldText>
                  <FormattedMessage
                    id="patient.immunisations.schedule.multipleDueDates"
                    defaultMessage="Mixed"
                  />
                </BoldText>
              ) : row.overallStatus === VaccineStatus.DUE ? (
                <BoldText>{format(row.nextDueDate, "dd/MM/yyyy")}</BoldText>
              ) : (
                <NonDueText>
                  {row.nextDueDate && format(row.nextDueDate, "dd/MM/yyyy")}
                </NonDueText>
              )}
              {row.isExpandable && (
                <ExpandButton variant="text" data-testid="expand-more-button">
                  <ExpandMoreIcon
                    style={{
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                </ExpandButton>
              )}
            </NextDueMessageContainer>
          </TableCellWithChevron>
        </StyledTableRow>
      );

      if (!row.isExpandable || !isExpanded) return [baseRow];

      return [
        baseRow,
        ...row.componentDiseases.map((disease, index) => (
          <TableRow key={`${row.id}-${disease.id}`}>
            <StyledTableCell
              noBorder={index !== row.componentDiseases.length - 1}
            >
              <Typography variant="body1" color="textSecondary">
                {disease.displayName}
              </Typography>
            </StyledTableCell>
            <StyledTableCell
              noBorder={index !== row.componentDiseases.length - 1}
            >
              <DoseCompleteIconsContainer>
                {disease.doseStatuses.map((doseStatus, index) => (
                  <span key={`${index}-${doseStatus}`}>
                    {doseStatusToIconMapper[doseStatus]}
                  </span>
                ))}
              </DoseCompleteIconsContainer>
            </StyledTableCell>
            <TableCellWithChevron
              noBorder={index !== row.componentDiseases.length - 1}
            >
              <NextDueMessageContainer>
                {vaccineStatusToStatusChipMapper[disease.status]}
                {disease.status === VaccineStatus.DUE ? (
                  <BoldText>{disease.message}</BoldText>
                ) : (
                  <NonDueText>{disease.message}</NonDueText>
                )}
              </NextDueMessageContainer>
            </TableCellWithChevron>
          </TableRow>
        )),
      ];
    });
  };

  return (
    <MuiTable component={Box}>
      <TableHead>
        <TableRow>
          <TableCell>Vaccine</TableCell>
          <TableCell>Doses</TableCell>
          <TableCell align="right" style={{ paddingRight: "54px" }}>
            Next Due
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>{renderRows()}</TableBody>
    </MuiTable>
  );
};
